import { ArrowUpIcon } from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { FooterContent } from '~/App/components/FooterContent'
import { Layout, LayoutContent, LayoutFooter } from '~/App/components/Layout'
import useHost from '~/App/Host/hooks/useHost'
import { useSessionChatThreads } from '~/App/SessionChatThreadsProvider'
import useSaveSessionChatThreadEvent from '~/App/SessionChatThreadsProvider/hooks/useSaveSessionChatThreadEvent'
import useApp from '~/App/slice/useApp'
import { Button } from '~/DesignSystem/components/button'
import { Messages } from '~/DesignSystem/components/messages'
import { Textarea } from '~/DesignSystem/components/textarea'
import { cn } from '~/DesignSystem/lib/utils'

import { MessagesSkeleton } from '../../components/messages/MessagesSkeleton'
import RespondingMessage from '../../components/messages/RespondingMessage'
import WelcomeMessage from '../../components/messages/WelcomeMessage'
import { Threads } from './components/Threads'
import { ThreadsScroll, type ThreadsScrollRefs } from './components/ThreadsScroll'

const newMessageAudio = new Audio('/new-message.mp3')

const RESPONDING_DELAY = 800

type ChatFormData = {
  message: string
}

export function ChatPage() {
  const intl = useIntl()
  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { isValid },
  } = useForm<ChatFormData>()
  const { widget } = useApp()

  const { items: inItems, hasMore, loaded, loading, loadMore } = useSessionChatThreads()

  const items = useMemo(() => {
    return inItems.slice().reverse()
  }, [inItems])

  const { firstEvent, latestEvent, latestChatterEvent } = useMemo(() => {
    return {
      firstEvent: items?.[0]?.attributes.events.data[0],
      latestEvent: items?.at(-1)?.attributes.events.data.at(-1),
      latestChatterEvent: items
        ?.at(-1)
        ?.attributes.events.data.slice()
        .reverse()
        .find(item => item.attributes.author?.data?.attributes.type === 'CHATTER'),
    }
  }, [items])

  const threadsScrollRefs = useRef<ThreadsScrollRefs>(null)

  const prevFirstEventId = useRef<string | undefined>(undefined)
  const prevLatestEventId = useRef<string | undefined>(undefined)
  const lastScrollHeight = useRef<number | undefined>(undefined)

  const scrollToBottom = (instant = false) => {
    requestAnimationFrame(() => {
      threadsScrollRefs.current?.bottom?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' })
    })
  }

  const { displayStatus } = useApp()

  useEffect(() => {
    if (displayStatus !== 'closed') {
      scrollToBottom(true)
    }
  }, [displayStatus])

  const setChatStatus = useHost('SET_CHAT_STATUS')

  useLayoutEffect(() => {
    if (firstEvent?.id !== prevFirstEventId.current) {
      threadsScrollRefs.current?.scroll?.scrollTo(
        0,
        (threadsScrollRefs.current?.scroll?.scrollHeight || 0) - (lastScrollHeight?.current || 0),
      )
    } else if (latestEvent?.id !== prevLatestEventId.current) {
      scrollToBottom()
      if (latestEvent?.attributes.author?.data?.attributes.type !== 'CUSTOMER') {
        newMessageAudio.play().catch(() => {})
        setChatStatus('unread')
      }
    }
    prevFirstEventId.current = firstEvent?.id
    prevLatestEventId.current = latestEvent?.id
    lastScrollHeight.current = threadsScrollRefs.current?.scroll?.scrollHeight
  }, [items])

  const [responding, setResponding] = useState(false)

  useEffect(() => {
    setResponding(false)
    if (latestEvent?.attributes.author?.data?.attributes.type === 'CUSTOMER') {
      setChatStatus('responding')
      const timeout = window.setTimeout(() => {
        scrollToBottom()
        setResponding(true)
      }, RESPONDING_DELAY)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [latestEvent?.attributes.author?.data?.attributes.type, latestEvent?.id])

  const {
    mutate: saveMessage,
    isPending: savingMessage,
    isSuccess: savedMessage,
  } = useSaveSessionChatThreadEvent()

  const onSubmit = async (data: ChatFormData) => {
    saveMessage(data.message)
  }

  useEffect(() => {
    if (savedMessage) {
      reset()
      requestAnimationFrame(() => {
        setFocus('message')
      })
    }
  }, [savedMessage])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.currentTarget.form?.requestSubmit()
    }
  }

  const handleOnBottom = () => {
    if (latestEvent?.attributes.author?.data?.attributes.type !== 'CUSTOMER') {
      setChatStatus('read')
    }
  }

  return (
    <Layout>
      <LayoutContent className="p-0">
        {!loaded && loading ? (
          <ThreadsScroll>
            <MessagesSkeleton />
          </ThreadsScroll>
        ) : (
          <ThreadsScroll
            ref={threadsScrollRefs}
            onTop={hasMore ? loadMore : undefined}
            onBottom={handleOnBottom}
          >
            <Messages>
              {!hasMore && <WelcomeMessage />}
              <Threads items={items} />
              {responding && (
                <RespondingMessage
                  owner={
                    latestChatterEvent?.attributes.author?.data?.attributes.display_name ||
                    undefined
                  }
                />
              )}
            </Messages>
          </ThreadsScroll>
        )}
      </LayoutContent>
      <LayoutFooter
        className={cn(
          'flex flex-col gap-2',
          widget?.attributes.mode === 'PLAYGROUND' && 'border-t-0',
        )}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="relative">
            <Textarea
              {...register('message', {
                required: true,
              })}
              className="resize-none h-28 pr-12"
              placeholder={intl.formatMessage({ id: 'chat.message.placeholder' })}
              onKeyDown={handleKeyDown}
              disabled={savingMessage}
            />
            <Button
              type="submit"
              className="absolute right-4 bottom-4 rounded-full"
              size="icon-lg"
              disabled={!isValid || savingMessage}
            >
              <ArrowUpIcon />
            </Button>
          </div>
        </form>
        {widget?.attributes.mode !== 'PLAYGROUND' && <FooterContent />}
      </LayoutFooter>
    </Layout>
  )
}
